import { useCallback, useEffect, useState } from "react";

import { appendAttempt, loadDailyState, type Attempt } from "../storage";

export interface UseDailyState {
  readonly attempts: readonly Attempt[];
  readonly addAttempt: (attempt: Attempt) => void;
}

/**
 * Reads + writes the attempt list for a given puzzle. Mounting (or
 * switching to a new puzzle id) lazily loads from localStorage; calling
 * `addAttempt` updates both storage and the in-memory state.
 *
 * The hook is intentionally not cross-tab-reactive — two tabs open to
 * the same puzzle will diverge until one reloads.
 */
export function useDailyState(puzzleId: string | undefined): UseDailyState {
  const [attempts, setAttempts] = useState<readonly Attempt[]>([]);

  useEffect(() => {
    const next = puzzleId === undefined ? [] : loadDailyState(puzzleId).attempts;
    // Bridging a non-reactive external source (localStorage) into React
    // state; safe because `loadDailyState` is pure and the puzzleId
    // dependency cuts the cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAttempts(next);
  }, [puzzleId]);

  const addAttempt = useCallback(
    (attempt: Attempt) => {
      if (puzzleId === undefined) return;
      const next = appendAttempt(puzzleId, attempt);
      setAttempts(next.attempts);
    },
    [puzzleId],
  );

  return { attempts, addAttempt };
}
